import Typography from "../../../../components/Typography/Typography";
import Button from "../../../../components/Button/Button";
import "./AIAssitPage.scss";
import BasicTable from "../../../../components/tables/table";
import { Icon } from "../../../../components/Icon";
import { useModal } from "../../../../hooks/modalsHooks";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getCreatedAtFromFormData } from "../../../../utils/aiAssistUtils";
import React from "react";

function ReviewEditAsset({
    extractedData,
    selectedAssetType,
    assetTypeList,
    onAssetUpdated,
    onAssetDeleted,
    originalExtractedCount,
    onRetry,
    isRetrying,
    isSubmitting = false,
    onDependencyWarning,
}) {
    const { showModal } = useModal();
    const { id } = useParams();

    const totalExtractedAssets = originalExtractedCount || 0;
    const validAssetsCount = extractedData ? extractedData.length : 0;

    const validateAssetDependencies = (asset) => {
        const dependencyConfig =
            selectedAssetType?.dependencyConfig?.dependencies ||
            asset?.asset_type?.dependency_config?.dependencies;
        const actualDependencies = asset?.dependencies || [];

        if (dependencyConfig && dependencyConfig.length > 0) {
            if (!actualDependencies || actualDependencies.length === 0) {
                return {
                    hasError: true,
                    message:
                        "This asset cannot be processed since the dependency was not added. Please configure dependency and create the asset.",
                };
            }
        }

        return { hasError: false, message: "" };
    };

    const assetsData = extractedData
        ? extractedData.map((asset, index) => {
              const dependencyValidation = validateAssetDependencies(asset);

              return {
                  asset_id: asset.asset_id || asset.assetId,
                  unit: asset.unit?.name || asset.unit?.symbol || "N/A",
                  qty: asset.qty,
                  dependencies: asset?.dependencies || [],
                  form_data: asset.form_data,
                  asset_images: asset.asset_images || [],
                  asset_type_id: asset.asset_type_id || selectedAssetType?.id,
                  created_at: getCreatedAtFromFormData(asset.form_data),
                  batch_name: asset.batch?.name || "N/A",
                  asset_type_name: asset.asset_type?.type_name || "N/A",
                  dependencies_display: Array.isArray(asset?.dependencies)
                      ? asset.dependencies.length > 0
                          ? asset.dependencies
                                .map((dep) => {
                                    const assetId =
                                        dep.asset_id ||
                                        dep.assetId ||
                                        dep.asset_uuid;
                                    const quantity =
                                        dep.qty || dep.quantity || "";
                                    const unit =
                                        dep.unit?.name ||
                                        dep.unit?.symbol ||
                                        dep.unit ||
                                        "";
                                    return quantity && unit
                                        ? `${assetId} (${quantity} ${unit})`
                                        : assetId;
                                })
                                .join(", ")
                          : "None"
                      : "None",
                  dependencyError: dependencyValidation,
                  originalAsset: asset,
              };
          })
        : [];

    const hasAnyDependencyErrors = assetsData.some(
        (asset) => asset.dependencyError?.hasError,
    );

    React.useEffect(() => {
        if (onDependencyWarning) {
            onDependencyWarning(hasAnyDependencyErrors);
        }
    }, [hasAnyDependencyErrors, onDependencyWarning]);

    const handleEditClick = (asset) => {
        if (isSubmitting || isRetrying) return;
        const originalAsset = extractedData.find(
            (item) => (item.asset_id || item.assetId) === asset.asset_id,
        );
        const allExtractedAssetsDependencies = extractedData
            .filter(
                (extractedAsset) =>
                    (extractedAsset.asset_id || extractedAsset.assetId) !==
                    asset.asset_id,
            )
            .map((extractedAsset) => {
                const formattedDependencies = [];

                if (selectedAssetType?.dependencyConfig?.dependencies) {
                    selectedAssetType.dependencyConfig.dependencies.forEach(
                        (depConfig) => {
                            const assetDeps = Array.isArray(
                                extractedAsset.dependencies,
                            )
                                ? extractedAsset.dependencies
                                : [];

                            const selectedAssets = assetDeps
                                .filter(
                                    (d) =>
                                        d.asset_id || d.assetId || d.asset_uuid,
                                )
                                .map((d) => ({
                                    asset_id:
                                        d.asset_id || d.assetId || d.asset_uuid,
                                    asset_uuid:
                                        d.asset_uuid || d.assetId || d.asset_id,
                                    quantity: d.qty || d.quantity,
                                    unit: d.unit,
                                }));

                            formattedDependencies.push({
                                ...depConfig,
                                selectedAssets: selectedAssets,
                            });
                        },
                    );
                }

                return {
                    assetId: extractedAsset.asset_id || extractedAsset.assetId,
                    dependencies: formattedDependencies,
                };
            });

        const assetWithFormattedDependencies = {
            ...originalAsset,
            dependencies: Array.isArray(originalAsset?.dependencies)
                ? originalAsset.dependencies
                : [],
        };

        showModal({
            modalType: "EditAssetModal",
            modalProps: {
                assetData: assetWithFormattedDependencies,
                id: id,
                selectedAssetType: selectedAssetType,
                assetTypeList: assetTypeList,
                onAssetUpdated: onAssetUpdated,
                allExtractedAssetsDependencies: allExtractedAssetsDependencies,
                currentAssetId: asset.asset_id,
            },
        });
    };

    const handleDeleteClick = (asset) => {
        if (isSubmitting || isRetrying) return;
        const assetIdToDelete = asset.asset_id || asset.assetId;
        onAssetDeleted(assetIdToDelete);
        toast.success(
            `Asset deleted successfully. ${validAssetsCount - 1} assets remaining.`,
        );
    };

    const renderAssetIdCell = ({ value, row }) => {
        const { dependencyError } = row.original;

        if (dependencyError?.hasError) {
            return (
                <div className="d-flex align-items-center gap-2">
                    <div className="position-relative">
                        <i
                            className="bi bi-exclamation-triangle-fill text-warning"
                            title={dependencyError.message}
                        />
                    </div>
                    <span>{value}</span>
                </div>
            );
        }

        return <span>{value}</span>;
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12 pt-2">
                    <Typography variant="h3" className="mb-2">
                        Review & Edit Assets
                    </Typography>
                    <Typography variant="body1" className="mb-4">
                        Review the extracted asset data and make any necessary
                        edits
                    </Typography>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-6 mb-3">
                    <div className="card text-center review-card">
                        <div className="card-body">
                            <Typography
                                variant="h2"
                                className="text-primary mb-1 m-0 p-0"
                            >
                                {totalExtractedAssets}
                            </Typography>
                            <Typography
                                variant="body1"
                                className="text-secondary m-0 p-0"
                            >
                                Assets Extracted
                            </Typography>
                            <Typography
                                variant="body2"
                                className="text-muted m-0 p-0 small"
                            >
                                Total from AI processing
                            </Typography>
                        </div>
                    </div>
                </div>
                {/* Valid Assets */}
                <div className="col-md-6 mb-3">
                    <div className="card text-center review-card">
                        <div className="card-body">
                            <Typography
                                variant="h2"
                                className={`mb-1 m-0 p-0 ${hasAnyDependencyErrors ? "text-warning" : "text-success"}`}
                            >
                                {validAssetsCount}
                            </Typography>
                            <Typography
                                variant="body1"
                                className="text-secondary m-0 p-0"
                            >
                                Selected Assets
                            </Typography>
                            <Typography
                                variant="body2"
                                className="text-muted m-0 p-0 small"
                            >
                                {hasAnyDependencyErrors
                                    ? "Some assets need dependencies"
                                    : totalExtractedAssets > validAssetsCount
                                      ? `${totalExtractedAssets - validAssetsCount} deleted`
                                      : "All assets valid"}
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12 d-flex justify-content-between align-items-center mb-3">
                    <Typography variant="h3" className="text-lg mb-0">
                        Extracted Assets
                    </Typography>
                    <Button
                        variant="secondary"
                        fullWidth={false}
                        onClick={onRetry}
                        isLoading={isRetrying}
                        loadingText="Reprocessing..."
                    >
                        {isRetrying ? "Retrying..." : "Retry Extraction"}
                    </Button>
                </div>
                {/* Warning Alert for Dependency Issues */}
                {hasAnyDependencyErrors && (
                    <div className="row mt-2 mb-2">
                        <div className="col-12">
                            <div
                                className="alert alert-warning d-flex align-items-center"
                                role="alert"
                            >
                                <div>
                                    <i
                                        className="bi bi-exclamation-triangle-fill text-warning"
                                        style={{
                                            width: "20px",
                                            height: "20px",
                                        }}
                                    />
                                    <strong> Dependency Warning </strong>
                                    <br /> Some assets require dependencies that
                                    haven't been configured. Please edit these
                                    assets to add the required dependencies
                                    before proceeding.
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className="col-12">
                    {!extractedData || extractedData.length === 0 ? (
                        <div className="text-center p-4">
                            <p className="text-muted mb-0">
                                No extracted assets available. Please complete
                                the AI extraction process first.
                            </p>
                        </div>
                    ) : (
                        <BasicTable
                            data={assetsData}
                            columns={[
                                {
                                    header: "Asset ID",
                                    accessor: "asset_id",
                                    Cell: renderAssetIdCell,
                                },
                                {
                                    header: "Quantity",
                                    accessor: "qty",
                                },
                                {
                                    header: "Unit",
                                    accessor: "unit",
                                },
                                {
                                    header: "Dependencies",
                                    accessor: "dependencies_display",
                                    Cell: ({ value }) => (
                                        <div
                                            className="text-truncate"
                                            title={value}
                                            style={{ maxWidth: "200px" }}
                                        >
                                            {value}
                                        </div>
                                    ),
                                },
                                {
                                    header: "Asset Type",
                                    accessor: "asset_type_name",
                                },
                                {
                                    header: "Date",
                                    accessor: "created_at",
                                },
                                {
                                    header: "",
                                    accessor: "action",
                                    Cell: ({ row }) => (
                                        <div className="d-flex flex-row justify-content-end">
                                            <Icon
                                                src="/icons/edit.svg"
                                                className={`mx-2 ${isSubmitting || isRetrying ? "opacity-50" : ""}`}
                                                height={15}
                                                width={15}
                                                onClick={() => {
                                                    handleEditClick(
                                                        row.original,
                                                    );
                                                }}
                                                style={{
                                                    cursor:
                                                        isSubmitting ||
                                                        isRetrying
                                                            ? "not-allowed"
                                                            : "pointer",
                                                    pointerEvents:
                                                        isSubmitting ||
                                                        isRetrying
                                                            ? "none"
                                                            : "auto",
                                                }}
                                                disabled={
                                                    isSubmitting || isRetrying
                                                }
                                            />
                                            <Icon
                                                src="/icons/delete.svg"
                                                className={`mx-2 ${isSubmitting || isRetrying ? "opacity-50" : ""}`}
                                                height={15}
                                                width={15}
                                                onClick={() => {
                                                    handleDeleteClick(
                                                        row.original,
                                                    );
                                                }}
                                                style={{
                                                    cursor:
                                                        isSubmitting ||
                                                        isRetrying
                                                            ? "not-allowed"
                                                            : "pointer",
                                                    pointerEvents:
                                                        isSubmitting ||
                                                        isRetrying
                                                            ? "none"
                                                            : "auto",
                                                }}
                                                disabled={
                                                    isSubmitting || isRetrying
                                                }
                                            />
                                        </div>
                                    ),
                                },
                            ]}
                            isSortable={false}
                            isPaginated={false}
                            pageSize={10}
                            currentPage={0}
                            showCheckBox={false}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default ReviewEditAsset;
